import React from 'react';
import { Target, BookOpen, Link, Clock, User, Calendar } from 'lucide-react';
import logoImage from '../assets/logo.png';

interface Competency {
  id: number;
  name: string;
  description?: string;
  parent_id?: number;
  sort_order: number;
  children?: Competency[];
}

interface ContentField {
  id: number;
  name: string;
  description?: string;
  content_type: string;
  difficulty_level: string;
  estimated_duration?: number;
  materials_needed?: string;
  learning_objectives?: string;
  assessment_criteria?: string;
  sort_order: number;
}

interface Mapping {
  id: number;
  competency_id: number;
  content_field_id: number;
  relationship_type: string;
  strength: number;
  description?: string;
  competency_name: string;
  competency_description?: string;
  content_field_name: string;
  content_field_description?: string;
  content_type: string;
  sort_order: number;
}

interface Curriculum {
  id: number;
  cid?: string;
  name: string;
  subject: string;
  exam_type?: string;
  updated_at?: string;
}

interface CurriculumPrintViewProps {
  curriculum: Curriculum;
  competencies: Competency[];
  contentFields: ContentField[];
  mappings: Mapping[];
}

export const CurriculumPrintView: React.FC<CurriculumPrintViewProps> = ({
  curriculum,
  competencies,
  contentFields,
  mappings
}) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getContentTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      'practical': 'Praktisch',
      'theoretical': 'Theoretisch',
      'assessment': 'Bewertung',
      'project': 'Projekt',
      'experiment': 'Experiment'
    };
    return types[type] || type;
  };

  const getDifficultyLabel = (level: string) => {
    const levels: Record<string, string> = {
      'beginner': 'Anfänger',
      'intermediate': 'Fortgeschritten',
      'advanced': 'Experte'
    };
    return levels[level] || level;
  };

  const getRelationshipLabel = (type: string) => {
    const types: Record<string, string> = {
      'direct': 'Direkt',
      'supporting': 'Unterstützend',
      'prerequisite': 'Voraussetzung',
      'assessment': 'Bewertung'
    };
    return types[type] || type;
  };

  const renderCompetencyTree = (competencies: Competency[], level = 0) => {
    return competencies.map((competency) => (
      <div key={competency.id} className={`competency-item mb-3 ${level > 0 ? 'ml-6' : ''}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-1">
            <Target className="w-3 h-3 text-blue-600" />
          </div>
          <div className="flex-1">
            <h4 className={`font-semibold text-gray-900 ${level === 0 ? 'text-lg' : 'text-base'}`}>
              {competency.name}
            </h4>
            {competency.description && (
              <p className="text-sm text-gray-600 mt-1">{competency.description}</p>
            )}
          </div>
        </div>
        {competency.children && competency.children.length > 0 && (
          <div className="mt-2">
            {renderCompetencyTree(competency.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="print-container bg-white p-8 max-w-4xl mx-auto">
      {/* PDF Header with Logo */}
      <div className="pdf-header flex items-center justify-between mb-6 pb-4 border-b-2 border-primary-500">
        <div className="flex items-center gap-3">
          <img 
            src={logoImage} 
            alt="Lernrakete Logo" 
            className="h-12 w-12 object-contain"
            style={{ width: '5cm', height: '5cm' }}
          />
          <div className="text-left">
            <h2 className="text-lg font-bold text-primary-600">Lernrakete</h2>
            <p className="text-xs text-gray-500">Curriculum-Dokumentation</p>
          </div>
        </div>
        <div className="text-right text-sm text-gray-600">
          <p className="font-medium">{formatDate(curriculum.updated_at)}</p>
        </div>
      </div>

      {/* Main Header */}
      <div className="text-center mb-8 pb-6 border-b border-gray-200">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{curriculum.name}</h1>
        <div className="flex items-center justify-center space-x-4 text-gray-600">
          <span className="flex items-center">
            <BookOpen className="w-4 h-4 mr-1" />
            {curriculum.subject}
          </span>
          {curriculum.exam_type && (
            <span className="flex items-center">
              <User className="w-4 h-4 mr-1" />
              {curriculum.exam_type}
            </span>
          )}
          {curriculum.cid && (
            <span className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {curriculum.cid}
            </span>
          )}
        </div>
      </div>

      {/* Kompetenzen */}
      <div className="mb-8 section-content">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center section-heading">
          <Target className="w-6 h-6 mr-2 text-blue-600" />
          Kompetenzen
        </h2>
        <div className="bg-gray-50 rounded-lg p-6">
          {competencies.length === 0 ? (
            <p className="text-gray-500 italic">Keine Kompetenzen definiert</p>
          ) : (
            renderCompetencyTree(competencies)
          )}
        </div>
      </div>

      {/* Inhaltsfelder */}
      <div className="mb-8 section-content">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center section-heading">
          <BookOpen className="w-6 h-6 mr-2 text-green-600" />
          Inhaltsfelder
        </h2>
        <div className="space-y-4">
          {contentFields.length === 0 ? (
            <p className="text-gray-500 italic">Keine Inhaltsfelder definiert</p>
          ) : (
            contentFields.map((field) => (
              <div key={field.id} className="content-field-item bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{field.name}</h3>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getContentTypeLabel(field.content_type)}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {getDifficultyLabel(field.difficulty_level)}
                    </span>
                    {field.estimated_duration && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        <Clock className="w-3 h-3 mr-1" />
                        {field.estimated_duration} min
                      </span>
                    )}
                  </div>
                </div>
                {field.description && (
                  <p className="text-gray-600 mb-3">{field.description}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {field.materials_needed && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">Benötigte Materialien:</h4>
                      <p className="text-gray-600">{field.materials_needed}</p>
                    </div>
                  )}
                  {field.learning_objectives && (
                    <div>
                      <h4 className="font-medium text-gray-700 mb-1">Lernziele:</h4>
                      <p className="text-gray-600">{field.learning_objectives}</p>
                    </div>
                  )}
                  {field.assessment_criteria && (
                    <div className="md:col-span-2">
                      <h4 className="font-medium text-gray-700 mb-1">Bewertungskriterien:</h4>
                      <p className="text-gray-600">{field.assessment_criteria}</p>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Mappings */}
      <div className="mb-8 section-content">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center section-heading">
          <Link className="w-6 h-6 mr-2 text-purple-600" />
          Kompetenz-Inhaltsfeld Beziehungen
        </h2>
        <div className="space-y-3">
          {mappings.length === 0 ? (
            <p className="text-gray-500 italic">Keine Beziehungen definiert</p>
          ) : (
            mappings.map((mapping) => (
              <div key={mapping.id} className="mapping-item bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 text-sm">{mapping.competency_name}</h4>
                      {mapping.competency_description && (
                        <p className="text-xs text-gray-600">{mapping.competency_description}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <span className="text-gray-400">→</span>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                    </div>
                    <div className="text-center">
                      <h4 className="font-semibold text-gray-900 text-sm">{mapping.content_field_name}</h4>
                      {mapping.content_field_description && (
                        <p className="text-xs text-gray-600">{mapping.content_field_description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getRelationshipLabel(mapping.relationship_type)}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {Math.round(mapping.strength * 100)}%
                    </span>
                  </div>
                </div>
                {mapping.description && (
                  <p className="text-sm text-gray-600 mt-2 italic">"{mapping.description}"</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="pdf-footer border-t-2 border-primary-500 pt-4 mt-8">
        {/* Generation Info */}
        <div className="text-center text-sm text-gray-500 mb-4">
          <p>Generiert am {new Date().toLocaleDateString('de-DE')} um {new Date().toLocaleTimeString('de-DE')}</p>
        </div>
        
        {/* Branding Section with Logo */}
        <div className="flex items-center justify-center gap-4 py-4 border-t border-gray-200">
          <img 
            src={logoImage} 
            alt="Lernrakete Logo" 
            className="object-contain"
            style={{ width: '5cm', height: '5cm' }}
          />
          <div className="text-left">
            <p className="font-bold text-sm text-primary-600">Lernrakete</p>
            <p className="text-xs text-gray-500">Die digitale Lernplattform für individualisiertes Lernen</p>
            <p className="text-xs text-primary-600 font-medium mt-1">www.lernrakete.com</p>
          </div>
        </div>
        
        {/* Copyright */}
        <div className="text-center text-xs text-gray-400 pt-3 border-t border-gray-100">
          <p>© {new Date().getFullYear()} Lernrakete. Alle Rechte vorbehalten.</p>
        </div>
      </div>

      <style>{`
        /* PDF Header and Footer styling */
        .pdf-header {
          background: linear-gradient(to right, #f0f9ff, #ffffff);
        }
        
        .pdf-footer {
          background: linear-gradient(to right, #ffffff, #f0f9ff);
        }
        
        @media print {
          /* Set page margins to 2cm */
          @page {
            margin: 2cm;
            size: A4 portrait;
          }
          
          .print-container {
            margin: 0;
            padding: 0;
            max-width: none;
          }
          
          .no-print {
            display: none !important;
          }
          
          body {
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
            margin: 0;
            padding: 0;
          }
          
          /* PDF Header - fixed at top */
          .pdf-header {
            position: relative;
            margin-bottom: 20px;
            page-break-after: avoid;
            break-after: avoid;
          }
          
          /* PDF Footer - keep at bottom */
          .pdf-footer {
            margin-top: 40px;
            page-break-before: avoid;
            break-before: avoid;
          }
          
          /* Prevent page breaks inside elements */
          .competency-item,
          .content-field-item,
          .mapping-item {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Section headings should not break from content */
          .section-heading {
            page-break-after: avoid;
            break-after: avoid;
            orphans: 4;
          }
          
          /* Keep section content together when possible */
          .section-content {
            page-break-inside: avoid;
            break-inside: auto;
          }
          
          /* Add page breaks before major sections if needed */
          .section-break {
            page-break-before: auto;
          }
          
          /* Ensure colors and gradients are printed */
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Ensure logo images are printed */
          img {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            max-width: 100%;
            height: auto;
          }
        }
      `}</style>
    </div>
  );
};
